import { Stack } from '@mantine/core';
import type { PaymentPlanPayment } from 'api/models';
import Invoice from 'app/Private/Invoices/Invoice';
import classNames from 'classnames/bind';
import { Panel } from 'components/Panel';
import dayjs from 'dayjs';
import { useCompanyConfiguration } from 'init/use-company-configuration';
import { i18nKeys, useTranslation } from 'locales';
import Amount from 'shared/components/Amount';
import type { AttachedInvoices } from 'shared/hooks/schemas/attached-invoices';
import { currencySymbol } from 'shared/utils/currency';
import { DialogShowId, DialogShowSize, showDialog } from 'store/view/view.actions';
import type { CurrencyCode } from 'types/currency';

import styleIdentifiers from './AttachedInvoices.module.scss';

const styles = classNames.bind(styleIdentifiers);

type Props = {
  invoices: AttachedInvoices & { total_due_without_fees?: number };
  creditNotes?: Array<PaymentPlanPayment>;
  payments?: Array<PaymentPlanPayment>;
  withoutFees?: boolean;
};

export default function AttachedInvoicesList({
  invoices,
  withoutFees,
  creditNotes,
  payments,
}: Props) {
  const { t } = useTranslation();
  const company = useCompanyConfiguration();

  const showInvoice = (invoice_id: string) => () => {
    showDialog({
      id: DialogShowId.INVOICE,
      size: DialogShowSize.LARGE,
      children: <Invoice id={invoice_id} modal />,
    });
  };

  const showCreditNote = (id: string | number) => () => {
    showDialog({
      id: DialogShowId.INVOICE,
      size: DialogShowSize.LARGE,
      children: <Invoice id={id} modal isCreditNote />,
    });
  };

  return (
    <Stack>
      <Panel
        withBorder
        title={t(
          `THIRD_PARTY_CASES.INVOICES_OF_CASE.${invoices.details.length > 1 ? 'MANY' : 'ONE'}`,
        )}
      >
        <div className={styles('attached-invoices')}>
          <table>
            <thead>
              <tr>
                <th>{t(i18nKeys.FORM.REFERENCE)}</th>
                <th>{t(i18nKeys.ISSUE_DATE)}</th>
                <th>{t(i18nKeys.DUE_DATE)}</th>
                <th>{t(i18nKeys.TOTAL_VAT_PRICE)}</th>
                {!withoutFees && <th>{t(i18nKeys.LATE_FEES)}</th>}
                <th>{t(i18nKeys.INVOICING.ALREADY_PAID)}</th>
                <th>
                  {t(i18nKeys.BALANCE_CURRENCY, {
                    text: currencySymbol(undefined, company),
                  })}
                </th>
              </tr>
            </thead>
            <tbody>
              {invoices.details.map(invoice => (
                <tr
                  key={invoice.id}
                  className={styles('invoice-line')}
                  onClick={invoice.deleted ? undefined : showInvoice(invoice.id)}
                  style={{ cursor: invoice.deleted ? 'default' : 'pointer' }}
                >
                  <td>
                    {invoice.reference} {invoice.deleted && `(${t(i18nKeys.INVOICE.DELETED)})`}
                  </td>
                  <td>{dayjs(invoice.issue_date).format('L')}</td>
                  <td>{dayjs(invoice.due_date).format('L')}</td>
                  <td>
                    <Amount
                      localizedValue={invoice.total_tvac}
                      value={invoice.localized_money_object.total_tvac}
                      suffix={invoice.currency}
                    />
                  </td>
                  {!withoutFees && (
                    <td>
                      <Amount
                        localizedValue={invoice.late_fees}
                        value={invoice.localized_money_object.late_fees}
                        suffix={invoice.currency}
                      />
                    </td>
                  )}
                  <td>
                    <Amount
                      localizedValue={invoice.amount_already_paid || 0}
                      value={invoice.localized_money_object.amount_already_paid || 0}
                      suffix={invoice.currency}
                    />
                  </td>
                  <td>
                    <Amount
                      localizedValue={
                        withoutFees
                          ? invoice.remaining_balance
                          : invoice.remaining_balance_with_fees
                      }
                      value={
                        withoutFees
                          ? invoice.localized_money_object.remaining_balance
                          : invoice.localized_money_object.remaining_balance_with_fees
                      }
                      suffix={invoice.currency}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
            {invoices.details.length > 1 && (
              <tfoot>
                <tr className={styles('total-line')}>
                  <td aria-hidden="true" />
                  <td aria-hidden="true" />
                  <td aria-hidden="true" />
                  <td>
                    <Amount
                      localizedValue={invoices.total_tvac}
                      value={invoices.total_tvac}
                      suffix={company?.currency}
                    />
                  </td>
                  {!withoutFees && (
                    <td>
                      <Amount
                        localizedValue={invoices.total_late_fees}
                        value={invoices.total_late_fees}
                        suffix={company?.currency}
                      />
                    </td>
                  )}
                  <td>
                    <Amount
                      localizedValue={invoices.paid_amount}
                      value={invoices.paid_amount}
                      suffix={company?.currency}
                    />
                  </td>
                  <td>
                    <Amount
                      localizedValue={
                        withoutFees ? invoices.total_due_without_fees : invoices.total_due
                      }
                      value={withoutFees ? invoices.total_due_without_fees : invoices.total_due}
                      suffix={company?.currency}
                    />
                  </td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </Panel>
      {creditNotes && creditNotes.length > 0 && (
        <Panel title={t(i18nKeys.INVOICING.CREDIT_NOTE_BOOK)}>
          <div className={styles('attached-invoices')}>
            <table>
              <thead>
                <tr>
                  <th>{t(i18nKeys.FORM.REFERENCE)}</th>
                  <th>{t(i18nKeys.DUE_DATE)}</th>
                  <th>{t(i18nKeys.TOTAL_VAT_PRICE)}</th>
                  <th>{t(i18nKeys.CLIENT.DETAIL.REMAIN_BALANCE)}</th>
                </tr>
              </thead>
              <tbody>
                {creditNotes.map(creditNote => (
                  <tr
                    key={creditNote.id}
                    className={styles('invoice-line')}
                    onClick={showCreditNote(creditNote.id)}
                  >
                    <td>{creditNote.reference}</td>
                    <td>{dayjs(creditNote.received_at).format('L')}</td>
                    <td>
                      <Amount
                        value={creditNote.original_currency.total}
                        suffix={creditNote.original_currency.currency as CurrencyCode}
                      />
                    </td>
                    <td>
                      <Amount
                        value={creditNote.original_currency.remaining_balance}
                        suffix={creditNote.original_currency.currency as CurrencyCode}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Panel>
      )}
      {payments && payments.length > 0 && (
        <Panel title="Paiements">
          <div className={styles('attached-invoices')}>
            <table>
              <thead>
                <tr>
                  <th>{t(i18nKeys.FORM.REFERENCE)}</th>
                  <th>{t(i18nKeys.DUE_DATE)}</th>
                  <th>{t(i18nKeys.TOTAL_VAT_PRICE)}</th>
                  <th>{t(i18nKeys.CLIENT.DETAIL.REMAIN_BALANCE)}</th>
                </tr>
              </thead>
              <tbody>
                {payments.map(payment => (
                  <tr
                    key={payment.id}
                    className={styles('invoice-line')}
                    style={{ cursor: 'default' }}
                  >
                    <td>{payment.reference}</td>
                    <td>{dayjs(payment.received_at).format('L')}</td>
                    <td>
                      <Amount
                        value={payment.original_currency.total}
                        suffix={payment.original_currency.currency as CurrencyCode}
                      />
                    </td>
                    <td>
                      <Amount
                        value={payment.original_currency.remaining_balance}
                        suffix={payment.original_currency.currency as CurrencyCode}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Panel>
      )}
    </Stack>
  );
}
